import React, { MouseEvent, useState, FC } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import {
  useInput,
  useMultiDivisionMultiSectionDropdown,
  useQuestionAnswerForms,
  useApisManagersQuestionAnswerCategoriesUpdate,
  useBoolean,
} from "~/hooks";

import { NoDataDisplay } from "~/components/organisms";
import { AllDistributionCheckboxMultiParentsWithMultiChildrenDropdownField } from "~/components/organisms";

import { QuestionAnswerForm } from "./QuestionAnswerForm";

import {
  QuestionAnswerCategoryWithQuestionAnswerType,
  EmployeeProfileType,
  QuestionAnswerTagType,
  MultiValueType,
  OptionType,
} from "~/domains";

import { PublicStatus } from "~/domains/PublicStatus";
import {
  LabelWithTextField,
  TabList,
  AddFormFieldButton,
  NameOrderableForm,
} from "~/ui";
import { VerticalProgressLine } from "~/ui";
import { Button, DragDropProvider } from "~/ui";

type PropsType = {
  currentEmployee: EmployeeProfileType;
  questionAnswerCategory: QuestionAnswerCategoryWithQuestionAnswerType;
  selectableQuestionAnswerTags: QuestionAnswerTagType[];
};

export const QuestionAnswerCategoryWithQuestionAnswersForm: FC<PropsType> = ({
  currentEmployee,
  questionAnswerCategory,
  selectableQuestionAnswerTags,
}: PropsType) => {
  const navigate = useNavigate();
  const { mutate: updateRequest, isLoading: isUpdating } =
    useApisManagersQuestionAnswerCategoriesUpdate();

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);

  const [{ value: name, onChange: onChangeName }] = useInput(
    questionAnswerCategory.name,
  );

  const {
    isChecked: allBusinessSectionsDistribution,
    handleChange: onChangeAllBusinessSectionsDistribution,
  } = useBoolean(questionAnswerCategory.allBusinessSectionsDistribution);

  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee.businessDivisions || [],
    selectableSections: currentEmployee.businessSections || [],
    defaultDivisions: questionAnswerCategory.businessDivisions,
    defaultSections: questionAnswerCategory.businessSections,
  });

  const {
    isSubmitting,
    questionAnswers,
    selectableQuestionAnswerTagOptions,
    onChangeQuestionContent,
    onChangeAnswerContent,
    saveQuestionAnswer,
    addQuestionAnswer,
    removeQuestionAnswer,
    changeDisplayOrder,
    setNotReadOnly,
    setReadOnly,
    handleTagNameCreate,
    onChangeTags,
    onChangeLink,
  } = useQuestionAnswerForms({
    questionAnswerCategoryId: questionAnswerCategory.id,
    questionAnswers: questionAnswerCategory.questionAnswers,
    selectableQuestionAnswerTags,
  });
  const handleDraftSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const isConfirm = confirm(
      "作成・編集した「各項目」を「保存」しなければ反映されません。下書き保存を実行してよろしいですか？",
    );
    if (!isConfirm) return;

    handleSubmit(PublicStatus.DRAFTED.id);
  };

  const handlePublishSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const isConfirm = confirm(
      "作成・編集した「各項目」を「保存」しなければ反映されません。配布を実行してよろしいですか？",
    );
    if (!isConfirm) return;

    handleSubmit(PublicStatus.PUBLISHED.id);
  };

  const handleSubmit = (publicStatusId: number) => {
    const body = {
      businessSectionIds: optionSelectedSections.map((section) => section.id),
      name: name,
      publicStatusId: publicStatusId,
      allBusinessSectionsDistribution,
    };
    updateRequest(
      {
        id: questionAnswerCategory.id,
        body,
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          navigate("/managers/question_answer_categories");
        },
      },
    );
  };

  const isCurrentQuestionAnswer = selectedTabIndex === 0;
  const savedQuestionAnswers = questionAnswers.filter(
    (questionAnswer) => !questionAnswer.isNew,
  );
  return (
    <>
      <div className="space-y-6">
        <AllDistributionCheckboxMultiParentsWithMultiChildrenDropdownField
          allParents={selectableDivisions}
          parentsValue={selectedDivisions}
          parentsOnChange={onDivisionChange}
          parentRequired={true}
          parentLabel="配布先: 部署"
          allChildren={optionSelectableSections}
          childrenValue={optionSelectedSections}
          childrenOnChange={onSectionChange}
          childRequired={true}
          childLabel="配布先: 課"
          allBusinessSectionsDistribution={allBusinessSectionsDistribution}
          onChangeAllBusinessSectionsDistribution={
            onChangeAllBusinessSectionsDistribution
          }
          currentEmployeeRole={currentEmployee.employeeRole}
        />
        <LabelWithTextField
          labelText="タイトル"
          type="text"
          name="name"
          placeholder="タイトル"
          required
          value={name}
          onChange={onChangeName}
        />
        <TabList
          tabTexts={["作成", "並び替え"]}
          onClick={(index: number) => setSelectedTabIndex(index)}
          selectedIndex={selectedTabIndex}
        />
        {isCurrentQuestionAnswer ? (
          questionAnswers.length ? (
            <VerticalProgressLine>
              {questionAnswers.map((questionAnswer) => (
                <QuestionAnswerForm
                  isSubmitting={isSubmitting}
                  defaultAccordionOpen={Boolean(questionAnswer.isNew)}
                  key={questionAnswer.id}
                  questionAnswer={questionAnswer}
                  remove={() => removeQuestionAnswer(questionAnswer)}
                  save={() => saveQuestionAnswer(questionAnswer)}
                  setNotReadOnly={() => setNotReadOnly(questionAnswer)}
                  setReadOnly={() => setReadOnly(questionAnswer)}
                  onChangeQuestionContent={(newValue: string) =>
                    onChangeQuestionContent({
                      questionAnswer,
                      newValue,
                    })
                  }
                  onChangeAnswerContent={(newValue: string) =>
                    onChangeAnswerContent({
                      questionAnswer,
                      newValue,
                    })
                  }
                  handleTagNameCreate={(newValue: string) =>
                    handleTagNameCreate({
                      questionAnswer,
                      newValue,
                    })
                  }
                  onChangeTags={(newValue: MultiValueType<OptionType>) =>
                    onChangeTags({
                      questionAnswer,
                      newValue,
                    })
                  }
                  onChangeLink={(newValue: string) =>
                    onChangeLink({
                      questionAnswer,
                      newValue,
                    })
                  }
                  selectableQuestionAnswerTagOptions={
                    selectableQuestionAnswerTagOptions
                  }
                />
              ))}
            </VerticalProgressLine>
          ) : (
            <NoDataDisplay message="Q&Aがありません" />
          )
        ) : savedQuestionAnswers.length ? (
          <DragDropProvider>
            <VerticalProgressLine>
              {savedQuestionAnswers.map((questionAnswer, index) => (
                <NameOrderableForm
                  key={`questionAnswer${questionAnswer.id}`}
                  name={questionAnswer.questionContent}
                  index={index}
                  changeDisplayOrder={(beforeIndex: number, newIndex: number) =>
                    changeDisplayOrder(beforeIndex, newIndex)
                  }
                />
              ))}
            </VerticalProgressLine>
          </DragDropProvider>
        ) : (
          <NoDataDisplay message="保存されたQ&Aがありません" />
        )}
        {isCurrentQuestionAnswer && (
          <AddFormFieldButton onClick={addQuestionAnswer} />
        )}
        <div className="flex items-center space-x-4 mx-auto">
          <Button
            text="下書き保存"
            color="gray"
            outline
            className="w-full"
            onClick={handleDraftSubmit}
            readonly={isUpdating}
          />
          <Button
            text="配布をする"
            color="primary"
            className="w-full"
            onClick={handlePublishSubmit}
            readonly={isUpdating}
          />
        </div>
      </div>
    </>
  );
};
